<template>
  <section class="boxes order-item-wrapper">
    <div class="index-content centered-block">
      <b-col class="pl-sm-3 pr-sm-3 pl-0 pr-0 item-details">
        <div class="order-main mt-0">
          <div>
            <div class="row no-gutters">
              <div class="col-md-4 order-inner-container item-background-color">
                <img class="card-img product-image mb-0" :src="imageUrl" alt="" />
              </div>
              <div class="col-md-8 product-body py-2">
                <div class="card-body order-item-body">
                  <div class="d-flex flex-column flex-lg-row justify-content-between status name my-1">
                    <b class="product-name">
                      <template v-if="canUpdateBilling()">
                        <b-link @click="editItem(billingInput)">
                          <span v-html="orderItem.name"></span>
                        </b-link>
                      </template>
                      <template v-else>
                        <span v-html="orderItem.name"></span>
                      </template>
                    </b>
                  </div>
                  <div class="d-flex justify-content-between status w-100 mt-3">
                    <span>Order #</span>
                    <span>{{orderItem.order.display_order_id || orderItem.order.order_id}}</span>
                  </div>
                  <div v-if="orderItem.order.display_order_id && (orderItem.order.display_order_id !== orderItem.order.order_id)" class="d-flex justify-content-between status w-100">
                    <span>Order ID</span>
                    <span>{{orderItem.order.order_id}}</span>
                  </div>
                  <div class="status d-flex justify-content-between status w-100">
                    <span>Quantity</span>
                    <span>
                      <b-link v-if="canChangeQuantity()" @click="editItem(changeQuantityInput)">{{orderItem.quantity}}</b-link>
                      <template v-else>{{orderItem.quantity}}</template>
                    </span>
                  </div>
                  <template v-if="orderItem.variant && orderItem.variant.options">
                    <div v-for="option in orderItem.variant.options" :key="'variant-option-' + option.id">
                      <div v-if="!option.is_deleted" class="d-flex justify-content-between status w-100">
                        <span>{{option.name.toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))}}</span>
                        <span>{{option.value}}</span>
                      </div>
                    </div>
                  </template>
                  <div class="d-flex justify-content-between status w-100 total-price pt-0">
                    <span>Price</span>
                    <span>{{orderItem.price | currency(orderItem.order.currency)}}</span>
                  </div>
                  <div v-if="orderItem.shipping_price > 0" class="d-flex justify-content-between status w-100 total-price">
                    <span>Shipping Price</span>
                    <span>{{orderItem.shipping_price | currency(orderItem.order.currency)}}</span>
                  </div>
                  <div v-if="orderItem.amount_refunded > 0" class="d-flex justify-content-between status w-100 total-price">
                    <span>Refunded</span>
                    <span class="refund">{{orderItem.amount_refunded | currency(orderItem.order.currency)}}</span>
                  </div>
                  <div v-if="showSubscription" class="d-flex justify-content-between status w-100">
                    <span>Subscription</span>
                    <span>
                      {{status}}
                      <span :class="'item-status-icon ' + statusClass">
                        <font-awesome-icon :icon="['fas', 'circle']" class="ml-2"></font-awesome-icon>
                      </span>
                    </span>
                  </div>
                  <template v-if="orderItem.campaign_product.show_next_bill_price">
                    <template v-if="orderItem.can_cancel_subscription">
                      <div v-if="orderItem.next_bill_date" class="d-flex justify-content-between status w-100">
                        <span>Next Billing</span>
                          <span>
                          {{orderItem.next_cycle_payment_due | currency(orderItem.order.currency)}} on
                          <b-link v-if="canChangeBillingInterval()" @click="editItem(changeIntervalInput)" class="a-links">{{getUTCShortDate(orderItem.next_bill_date)}}</b-link>
                          <template v-else>{{getUTCShortDate(orderItem.next_bill_date)}}</template>
                        </span>
                      </div>
                      <div v-if="isSubscriptionPauseScheduled" class="d-flex justify-content-between status w-100">
                        Subscription paused until {{getUTCShortDate(item.pause_resume_date)}}
                      </div>
                    </template>
                    <div class="item-img-top-text" v-else-if="isSubscriptionCancelScheduled">
                      Subscription cancels on {{getUTCShortDate(item.cancel_date)}}
                    </div>
                  </template>
                  <div v-if="showFulfillment" class="d-flex justify-content-between status w-100">
                    <span>Shipment</span>
                    <span>
                      {{deliveryStatus || fulfillmentStatus}}
                      <span :class="'item-status-icon ' + fulfillmentStatusClass">
                        <font-awesome-icon :icon="['fas', 'circle']" class="ml-2"></font-awesome-icon>
                      </span>
                    </span>
                  </div>
                  <template v-if="orderItem.fulfillments && orderItem.fulfillments.length">
                    <template v-if="showTracking">
                      <div class="d-flex justify-content-between status w-100">
                        <span>Tracking #</span>
                        <b-link v-if="canTrack" @click="$bvModal.show('fulfillment-tracking-modal')" class="a-links">{{orderItem.fulfillments[0].tracking_number}}</b-link>
                        <span v-else>Awaiting Scan</span>
                      </div>
                    </template>
                    <template v-if="showRMANumber">
                      <div class="d-flex justify-content-between status w-100">
                        <span>RMA #</span>
                        <span>{{orderItem.fulfillments[0].rma_number}}</span>
                      </div>
                    </template>
                    <div v-if="orderItem.show_fulfillments" class="d-flex justify-content-between status w-100">
                      <span>Shipments</span>
                      <b-link @click="$bvModal.show('fulfillments-' + orderItem.id.toString())" class="a-links">View Shipments</b-link>
                    </div>
                  </template>
                  <template v-if="orderItem.show_transactions && orderItem.transactions && orderItem.transactions.length">
                    <div class="d-flex justify-content-between status w-100">
                      <span>Transactions</span>
                      <b-link @click="$bvModal.show('transactions-' + orderItem.id.toString())" class="a-links">View Transactions</b-link>
                    </div>
                  </template>
                  <template v-if="orderItem.club_id || orderItem.order.club_id">
                    <div class="d-flex justify-content-between status w-100">
                      <span>Membership</span>
                      <b-link @click="$bvModal.show('memberinfo-' + orderItem.id.toString())" class="a-links">View Membership</b-link>
                    </div>
                  </template>
                  <template v-if="orderItem.bundled_variants && orderItem.bundled_variants.length">
                    <div v-for="bundled_variant in orderItem.bundled_variants" :key="'bundled_variant-' + bundled_variant.variant.id" class="d-flex justify-content-between status w-100">
                      <span>{{getVariantOptions(bundled_variant.variant)}}</span>
                      <span>{{bundled_variant.quantity}}</span>
                    </div>
                  </template>
                  <div v-if="showNextCycle" class="d-flex justify-content-between status w-100 next-cycle-block">
                    <span>Next Cycle</span>
                    <span class="next-cycle-value">
                      <span v-if="showNextCycleProduct" class="next-cycle-main">
                        <img
                          class="next-cycle-thumb"
                          :src="nextCycleImageUrl"
                          @error="setNextCycleAltImg($event)"
                          alt=""
                        />
                        <span v-html="orderItem.next_cycle_product.name"></span>
                      </span>
                      <template v-if="showNextVariant">
                        <span
                          v-for="option in orderItem.next_variant.options"
                          :key="'next-variant-option-' + option.id"
                          class="next-cycle-variant-option"
                        >
                          <template v-if="!option.is_deleted">{{option.name}}: {{option.value}}</template>
                        </span>
                      </template>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Fulfillments :item="orderItem"></Fulfillments>
            <Transactions :item="orderItem"></Transactions>
            <MemberInfo v-if="orderItem.club_id || orderItem.order.club_id" :item="orderItem"></MemberInfo>
            <Tracking
              v-if="canTrack"
              :key="'item-' + orderItem.id.toString()"
              :fulfillment="orderItem.fulfillments[0]"
            ></Tracking>
          </div>
        </div>
      </b-col>
    </div>
  </section>
</template>

<script>
import {
  BillingCycleType, FulfillmentStatus, FulfillmentStatusStrings, InputType, ItemStatus
} from '@/constants'
import {
  getShortDate, getUTCShortDate, getItemStatus, getItemStatusClass, getItemFulfillmentStatus,
  getItemFulfillmentStatusClass, getItemImageUrl, showSubscription, showFulfillment, setFallbackImageUrl
} from '@/utils'
import {mapGetters} from 'vuex'
import axios from 'axios'
import Transactions from '@/components/Transactions'
import Fulfillments from '@/components/Fulfillments'

const Tracking = () => import('@/components/Tracking')
const MemberInfo = () => import('@/components/MemberInfo')

export default {
  name: 'OrderItemStatus',
  components: {
    Transactions,
    Fulfillments,
    Tracking,
    MemberInfo
  },
  props: {
    item: Object,
    session: Object
  },
  data: function() {
    return {
      getShortDate,
      getUTCShortDate,
      cancelInput: null,
      changeIntervalInput: null,
      changeQuantityInput: null,
      changeProductInput: null,
      billingInput: null,
      returnInput: null,
      orderItem: null
    }
  },
  computed: {
    ...mapGetters(['subscriptionStatusLabels']),
    status: function() {
      return getItemStatus(this.orderItem, this.subscriptionStatusLabels, false)
    },
    statusClass: function() {
      return getItemStatusClass(this.orderItem)
    },
    fulfillmentStatus: function() {
      return getItemFulfillmentStatus(this.orderItem)
    },
    fulfillmentStatusClass: function() {
      return getItemFulfillmentStatusClass(this.orderItem)
    },
    showFulfillment: function() {
      return showFulfillment(this.orderItem)
    },
    showSubscription: function() {
      return showSubscription(this.orderItem)
    },
    isSubscriptionPauseScheduled: function() {
      return (this.orderItem.status !== ItemStatus.Paused) && this.orderItem.pause_resume_date
    },
    isSubscriptionCancelScheduled: function() {
      return this.orderItem.cancel_date
    },
    billingCycleType: function() {
      let types = [
        'One-Time',
        'Recurring',
        'Multi-Pay',
        'One-Time'
      ]

      if (this.orderItem.billing_cycle_type === BillingCycleType.Recurring) {
        return 'Every ' + this.orderItem.billing_interval_days.toString() + ' days'
      }

      return types[this.orderItem.billing_cycle_type]
    },
    deliveryStatus: function() {
      let status = null

      if (
        [
          FulfillmentStatus.Shipped,
          FulfillmentStatus.Delivered,
          FulfillmentStatus.Returned
        ].indexOf(this.orderItem.fulfillment_status) !== -1 &&
        this.orderItem.fulfillments &&
        this.orderItem.fulfillments.length
      ) {
        status = this.getDeliveryStatus(this.orderItem.fulfillments[0])
      }

      return status
    },
    imageUrl: function() {
      return getItemImageUrl(this.orderItem, 'medium')
    },
    showNextCycleProduct: function() {
      return this.orderItem && this.orderItem.next_cycle_product && this.orderItem.product &&
        (this.orderItem.next_cycle_product.id !== this.orderItem.product.id)
    },
    showNextVariant: function() {
      return this.orderItem && this.orderItem.next_variant && this.orderItem.variant &&
        (this.orderItem.next_variant.id !== this.orderItem.variant.id)
    },
    showNextCycle: function() {
      return this.showNextCycleProduct || this.showNextVariant
    },
    nextCycleImageUrl: function() {
      return getItemImageUrl(this.orderItem, 'small', true)
    },
    canTrack: function() {
      return this.orderItem.fulfillments && this.orderItem.fulfillments.length &&
        this.orderItem.fulfillments[0].can_track
    },
    showTracking: function() {
      return this.canTrack || [
        FulfillmentStatus.Hold,
        FulfillmentStatus.PendingShipment,
        FulfillmentStatus.Processing,
        FulfillmentStatus.Frozen
      ].indexOf(this.orderItem.fulfillment_status) !== -1
    },
    showRMANumber: function() {
      if (this.orderItem.fulfillments && this.orderItem.fulfillments.length) {
        const fulfillment = this.orderItem.fulfillments[0]

        return ((fulfillment.status === FulfillmentStatus.RMAPending) ||
            (fulfillment.status === FulfillmentStatus.Returned)) &&
            fulfillment.rma_number && fulfillment.rma_number.length
      }

      return false
    }
  },
  methods: {
    getDeliveryStatus: function(fulfillment) {
      let status = FulfillmentStatusStrings[fulfillment.status]

      if ((fulfillment.status === FulfillmentStatus.Shipped) && fulfillment.ship_date) {
        status += ' ' + getShortDate(fulfillment.ship_date)
      } else if ((fulfillment.status === FulfillmentStatus.Delivered) && fulfillment.delivery_date) {
        status += ' ' + getShortDate(fulfillment.delivery_date)
      } else if ((fulfillment.status === FulfillmentStatus.Returned) && fulfillment.return_date) {
        status += ' ' + getShortDate(fulfillment.return_date)
      }

      return status
    },
    findAdditionalInput: function(type) {
      if (!this.session.additional_inputs) {
        return null
      }
      for (let input of this.session.additional_inputs) {
        if (input.type === type) {
          return input
        }
      }

      return null
    },
    canChangeBillingInterval: function() {
      return this.orderItem && this.orderItem.can_change_billing_interval &&
        this.isItemStatusOkForInput(this.cancelInput)
    },
    canChangeQuantity: function() {
      return this.orderItem && this.orderItem.can_change_quantity && this.isItemStatusOkForInput(this.cancelInput)
    },
    canUpdateBilling: function() {
      return this.orderItem && this.orderItem.is_active && this.isItemStatusOkForInput(this.billingInput)
    },
    editItem: function(input) {
      this.$emit('edit', input, this.orderItem)
    },
    isItemStatusOkForInput: function(input) {
      if (input) {
        if (input.item_statuses) {
          return (input.item_statuses.indexOf(this.orderItem.funnel_item_status) >= 0)
        } else {
          return true
        }
      }

      return false
    },
    getVariantOptions: function(variant) {
      let options = []
      variant.options.forEach(option => options.push(option.name + ': ' + option.value))

      return options.join(', ')
    },
    setNextCycleAltImg: function(event) {
      setFallbackImageUrl(event, this.orderItem, 'small', true, true)
    }
  },
  created() {
    this.cancelInput = this.findAdditionalInput(InputType.Cancel)
    this.changeIntervalInput = this.findAdditionalInput(InputType.ChangeInterval)
    this.changeQuantityInput = this.findAdditionalInput(InputType.ChangeQuantity)
    this.changeProductInput = this.findAdditionalInput(InputType.ChangeProduct)
    this.billingInput = this.findAdditionalInput(InputType.Billing)
    this.returnInput = this.findAdditionalInput(InputType.Return)
    this.orderItem = this.item

    console.log('item ' + this.item.id.toString())
    console.log(this.item)

    // get more item details
    axios.get('/order_items/' + this.item.id.toString() + '/')
      .then(result => {
        console.log(result)
        this.orderItem = result.data
      }, error => {
        console.error(error)
        this.processing = false
      })
  }
}
</script>

<style scoped lang="less">

  .order-inner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .product-image {
    width: 100%;
    max-height: 300px;
    object-fit: contain;
  }

  .order-item-body {
    padding:0 !important;
    padding-left: 16px !important;
  }

  .next-cycle-block {
    margin-top: 8px;
    align-items: flex-start;
  }

  .next-cycle-value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
  }

  .next-cycle-main {
    display: flex;
    align-items: center;
  }

  .next-cycle-thumb {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border: 1px solid #DFE0E0;
    margin-right: 8px;
    flex-shrink: 0;
    background: #fff;
  }

  .next-cycle-variant-option {
    display: block;
  }

  @media only screen and (max-width: 768px) {
    .order-item-wrapper {
      .order-item-body {
        padding: 24px 12px !important;
      }
    }
  }

  @media only screen and (max-width: 992px) {
    .order-item-wrapper {
      .product-status {
        float: none !important;
        text-align: left !important;

        .item-status-icon {
          float: none;
          margin-right: 10px;
        }

        .item-status-icon {
          svg {
            margin-left: 0 !important;
          }
        }
      }
    }
  }
</style>
